import React, { FC, useState } from "react"
import Loading from "../../../../ui/Loading"
import { KTitleSpan, KDropdown, KButton, KSpan, KTooltip, KInput } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { releases } from "../../../../../apis"
import { toast } from "react-toastify"
import { errorFilledToastOptions } from "../../../../../constants"
import { formatNumber } from "../../../../../utility"
import SearchUserComponent from "../../../../ui/components/SearchUserComponent"

type AdminKahunaEconomyProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
}

const formatOptions: KSelectOption[] = [
  { value: 1, label: "Raw", value2: "raw" },
  { value: 2, label: "Format", value2: "format" }
]

const AdminKahunaEconomy: FC<AdminKahunaEconomyProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [selectedFormatOption, setSelectedFormatOption] = useState(formatOptions[0])

  const downloadCSV = (data: string[][], fileName?: string, noFormat?: boolean) => {
    const csvContent =
      selectedFormatOption.value2 === "raw" || noFormat
        ? data.map((row) => row.map((item) => `"${item}"`).join(",")).join("\n")
        : data
            .map((row, csvIndex) =>
              row
                .map((item, index) => {
                  // Don't format the first columns: "Date", "Platform", "DSP" (if index < 3)
                  if (csvIndex === 0 || index === 0 || csvIndex === 11) {
                    return `"${item}"`
                  }
                  return `"${formatNumber(item, 2) || item}"`
                })
                .join(",")
            )
            .join("\n")

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.setAttribute("download", fileName || `kahuna_economy`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const onDownloadCsvMonthlyIncoming = (data: any) => {
    const monthList = data.map((dataRow: { [key: string]: number | string }) => `${dataRow.year_month || ""}`)

    const metricRows = [
      {
        key: "new_user_number",
        values: data.map((dataRow: { [key: string]: number | string }) => dataRow.non_admin_user_count ?? 0)
      },
      {
        key: "new_artist_number",
        values: data.map((dataRow: { [key: string]: number | string }) => dataRow.artist_user_number ?? 0)
      },
      {
        key: "new_release_number(based release date)",
        values: data.map((dataRow: { [key: string]: number | string }) => dataRow.total_release_count ?? 0)
      },
      {
        key: "new_takedown_release_number",
        values: data.map((dataRow: { [key: string]: number | string }) => dataRow.takedown_release_count ?? 0)
      },
      {
        key: "new_non_takedown_release_number",
        values: data.map((dataRow: { [key: string]: number | string }) => dataRow.normal_release_count ?? 0)
      },
      {
        key: "new_track_number",
        values: data.map((dataRow: { [key: string]: number | string }) => dataRow.total_track_count ?? 0)
      },
      {
        key: "new_takedown_release_track_number",
        values: data.map((dataRow: { [key: string]: number | string }) => dataRow.takedown_release_track_count ?? 0)
      },
      {
        key: "new_non_takedown_release_track_number",
        values: data.map((dataRow: { [key: string]: number | string }) => dataRow.normal_release_track_count ?? 0)
      },
      {
        key: "new_deal_number",
        values: data.map((dataRow: { [key: string]: number | string }) => dataRow.deal_number ?? 0)
      },
      {
        key: "new_advance_deal_number",
        values: data.map((dataRow: { [key: string]: number | string }) => dataRow.advance_deal_number ?? 0)
      },
      {
        key: "new_distribution_deal_number",
        values: data.map((dataRow: { [key: string]: number | string }) => dataRow.distribution_deal_number ?? 0)
      }
    ]

    const headers = ["", ...monthList].map((header: string) => header.replace("-", ""))

    const processedRows = metricRows.map((metricRow) => [metricRow.key, ...metricRow.values])

    const finalizedRows = [headers, ...processedRows]

    downloadCSV(finalizedRows, "kahuna_monthly_incoming_metrics", true)
  }

  const onExportCsv = () => {
    setLoading(true)

    releases(`/release/export_kahuna_economy/`, "get")
      .then((resp) => {
        setLoading(false)
        if (!resp.data.results) {
          toast.error("Error downloading csv", errorFilledToastOptions)
          return
        }
        downloadCSV(resp.data.results)
      })
      .catch((err) => {
        setLoading(false)
        toast.error("Error downloading csv", errorFilledToastOptions)
      })
  }

  const onExportMonthlyIncoming = () => {
    setLoading(true)

    releases(`/release/export_monthly_incoming_metrics/`, "get")
      .then((resp) => {
        setLoading(false)
        if (!resp.data.results) {
          toast.error("Error downloading csv", errorFilledToastOptions)
          return
        }
        onDownloadCsvMonthlyIncoming(resp.data.results)
      })
      .catch((err) => {
        setLoading(false)
        toast.error("Error downloading csv", errorFilledToastOptions)
      })
  }

  const renderDashboard = () => {
    return (
      <div className="flex flex-row gap-3">
        <div className="mt-8">
          <KButton
            dataTestId="export-csv-button"
            width="160px"
            background="black"
            textColor="white"
            text="Export CSV"
            onClick={() => onExportCsv()}
          />
        </div>
        <div className="mt-8">
          <KButton
            width="200px"
            background="black"
            textColor="white"
            text="Export Monthly Incoming Metrics (CSV)"
            onClick={() => onExportMonthlyIncoming()}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        <KTooltip
          backgroundColor="#000"
          padding="0px 4px"
          height="20px"
          arrowColor="#000"
          position="bottom"
          content={
            <div className="h-5 w-max flex items-center">
              <KSpan
                text={props.openSidebar ? "Hide sidebar" : "Open sidebar"}
                fontSize={12}
                lineHeight="16px"
                color="#FFF"
              />
            </div>
          }
        >
          <div className="flex items-center">
            <KButton
              dataTestId="reports-sidebar-toggle-button"
              icon="/left-sidebar.svg"
              width="32px"
              height="32px"
              background="transparent"
              shadowDisabled={true}
              padding="6px"
              onClick={() => props.setOpenSidebar(!props.openSidebar)}
            />
            <KTitleSpan fontSize={20} lineHeight="28px" text={"Kahuna Economy"} />
          </div>
        </KTooltip>
        <div className="flex gap-2">
          <div>
            <KDropdown
              width={110}
              options={formatOptions}
              selected={selectedFormatOption}
              onSelect={(selected) => setSelectedFormatOption(selected as KSelectOption)}
            />
          </div>
        </div>
      </div>
      {renderDashboard()}
    </div>
  )
}

export default AdminKahunaEconomy
